import React from 'react'
import {
    Control,
    Controller,
    UseFormWatch,
    UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Collapse, FormItem } from 'vnetwork-ui'
import { AlertTriangle, FileText } from 'lucide-react'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import DateTimePicker from '@/components/Form/DateTimePicker'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getServiceDowntimeLogs } from '@/modules/Service/services/sla'
import {
    DOWNTIME_CAUSE_LABELS,
    DOWNTIME_CAUSE_COUNTS_SLA,
    DowntimeCause,
    SLADowntimeLog,
} from '@/modules/Service/types/sla'

dayjs.extend(utc)

interface TicketDowntimeProps {
    control: Control<any>
    watch: UseFormWatch<any>
    setValue: UseFormSetValue<any>
    ticketRequestedAt?: Dayjs | null
    /** When editing existing ticket, pass ticketId to auto-detect downtime logs */
    ticketId?: number
}

const DOWNTIME_CAUSE_OPTIONS = [
    {
        value: 'provider',
        label: `${DOWNTIME_CAUSE_LABELS.provider} - DC, network, hardware`,
    },
    {
        value: 'customer',
        label: `${DOWNTIME_CAUSE_LABELS.customer} - Cấu hình sai, chưa thanh toán`,
    },
    {
        value: 'third_party',
        label: `${DOWNTIME_CAUSE_LABELS.third_party} - ISP, cloud provider bên ngoài`,
    },
    {
        value: 'force_majeure',
        label: `${DOWNTIME_CAUSE_LABELS.force_majeure} - Thiên tai, mất điện lưới`,
    },
    {
        value: 'scheduled',
        label: `${DOWNTIME_CAUSE_LABELS.scheduled} - Bảo trì đã thông báo trước`,
    },
]

const TicketDowntime: React.FC<TicketDowntimeProps> = ({
    control,
    watch,
    setValue,
    ticketRequestedAt,
    ticketId,
}) => {
    const { t } = useTranslation()

    const includeDowntime = watch('include_downtime')
    const downtimeCause = watch('downtime_cause') as DowntimeCause | undefined
    const countsAgainstSLA = watch('downtime_counts_against_sla')
    const serviceId = watch('service_id')

    // In edit mode: fetch existing downtime logs for this ticket
    const { data: logsData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SLA_DOWNTIME,
            serviceId,
            'ticket-downtime-edit',
            ticketId,
        ],
        queryFn: () =>
            getServiceDowntimeLogs(Number(serviceId), {
                page: 1,
                page_size: 10,
                q: '',
            }),
        enabled: !!ticketId && !!serviceId && Number(serviceId) > 0,
    })

    // Auto-enable toggle when ticket already has downtime logs (edit mode)
    const autoDetectedRef = React.useRef(false)
    React.useEffect(() => {
        if (!ticketId || autoDetectedRef.current) return
        const logs = logsData?.results || []
        const ticketLogs = logs.filter(
            (log: SLADowntimeLog) => log.ticket_id === ticketId
        )
        if (ticketLogs.length > 0) {
            autoDetectedRef.current = true
            const latestLog = ticketLogs[0]
            setValue('include_downtime', true)
            if (latestLog.downtime_cause) {
                setValue('downtime_cause', latestLog.downtime_cause)
            }
            if (latestLog.downtime_start) {
                setValue('downtime_start', dayjs.utc(latestLog.downtime_start))
            }
            if (latestLog.downtime_end) {
                setValue('downtime_end', dayjs.utc(latestLog.downtime_end))
            }
            setValue(
                'downtime_counts_against_sla',
                latestLog.counts_against_sla ?? true
            )
        }
    }, [ticketId, logsData, setValue])

    // Auto-set counts_against_sla based on cause
    React.useEffect(() => {
        if (downtimeCause && includeDowntime) {
            const shouldCount = DOWNTIME_CAUSE_COUNTS_SLA[downtimeCause]
            setValue('downtime_counts_against_sla', shouldCount)
        }
    }, [downtimeCause, includeDowntime, setValue])

    // Auto-set downtime_start from ticket_requested_at when enabled
    React.useEffect(() => {
        if (includeDowntime && ticketRequestedAt && !watch('downtime_start')) {
            setValue('downtime_start', ticketRequestedAt)
        }
    }, [includeDowntime, ticketRequestedAt, watch, setValue])

    return (
        <div className="space-y-2">
            {/* Toggle to include downtime */}
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    <div>
                        <Label className="text-sm font-medium text-orange-900">
                            {t(
                                'ticket.include_downtime',
                                'Ghi nhận Downtime SLA'
                            )}
                        </Label>
                        <p className="text-xs text-orange-700">
                            {t(
                                'ticket.include_downtime_hint',
                                'Tự động tạo log downtime khi tạo ticket'
                            )}
                        </p>
                    </div>
                </div>
                <Controller
                    name="include_downtime"
                    control={control}
                    render={({ field }) => (
                        <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                        />
                    )}
                />
            </div>

            {/* Downtime fields */}
            <Collapse open={includeDowntime}>
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {/* Cause */}
                    <FormItem
                        control={control}
                        name="downtime_cause"
                        type="select"
                        label={t('sla.downtime_cause', 'Nguyên nhân')}
                        placeholder="Chọn nguyên nhân"
                        options={DOWNTIME_CAUSE_OPTIONS}
                        allowClear={false}
                        size="small"
                    />

                    {/* Start Time */}
                    <DateTimePicker
                        control={control}
                        name="downtime_start"
                        label={t(
                            'sla.start_time',
                            'Thời gian bắt đầu downtime'
                        )}
                        size="small"
                        required
                    />

                    {/* End Time */}
                    <div>
                        <DateTimePicker
                            control={control}
                            name="downtime_end"
                            label={t('sla.end_time', 'Thời gian kết thúc')}
                            size="small"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {t(
                                'sla.leave_empty_ongoing',
                                'Để trống nếu đang diễn ra'
                            )}
                        </p>
                    </div>

                    {/* Info: Description uses ticket_affected */}
                    <div className="flex items-center gap-2 text-xs text-gray-600 p-2 bg-gray-100 rounded">
                        <FileText className="w-3.5 h-3.5" />
                        <span>
                            {t(
                                'ticket.downtime_uses_affected',
                                'Mô tả sự cố sẽ lấy từ "Ảnh hưởng" của ticket'
                            )}
                        </span>
                    </div>

                    {/* Counts Against SLA */}
                    <div
                        className={`flex items-center justify-between p-2 rounded-lg ${
                            countsAgainstSLA
                                ? 'bg-red-50 border border-red-200'
                                : 'bg-green-50 border border-green-200'
                        }`}
                    >
                        <div className="space-y-0.5">
                            <Label className="text-xs font-medium">
                                {t('sla.counts_against_sla', 'Tính vào SLA')}
                            </Label>
                            <p className="text-xs text-gray-600">
                                {countsAgainstSLA
                                    ? t(
                                          'sla.counts_yes_hint',
                                          'Ảnh hưởng uptime %'
                                      )
                                    : t(
                                          'sla.counts_no_hint',
                                          'Không ảnh hưởng uptime %'
                                      )}
                            </p>
                        </div>
                        <Controller
                            name="downtime_counts_against_sla"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                />
                            )}
                        />
                    </div>

                    {/* Info badge */}
                    <div className="flex items-center gap-2 text-xs text-blue-600">
                        <FileText className="w-3.5 h-3.5" />
                        <span>
                            {t(
                                'ticket.downtime_auto_link',
                                'Downtime sẽ được liên kết với ticket và service'
                            )}
                        </span>
                    </div>
                </div>
            </Collapse>
        </div>
    )
}

export default TicketDowntime
